import * as _ from 'lodash';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { ISSUE_EXCEPTION_PACKAGE_SORT } from '../../../../shared/issuesV2';
import { useQuery } from '../../../contexts/RouterContext';
import useDispatchOnMount from '../../../hooks/useDispatchOnMount';
import * as IssuesV2Store from '../../../redux/issuesV2';
import type {
  Category,
  IssueExceptionOrderBy,
  IssueExceptionSort,
  IssueExceptionsRemoveRequest,
} from '../../../../shared/issuesV2';
import type { FetchIssueExceptionPayload } from '../../../redux/issuesV2/actions';

export type UseIssueExceptionsResponse = {
  page: number;
  count: number;
  search: string;
  sortBy: IssueExceptionSort;
  onRemoveExceptions: (removeCriteria: IssueExceptionsRemoveRequest) => Promise<number>;
  setPage: (newPage: number) => void;
  setCount: (newCount: number) => void;
  setSearch: (newSearch: string) => void;
  setSort: (newSortBy: IssueExceptionSort) => void;
  onExtendException: (exceptionId: number, expiresAfter: string | null) => Promise<void>;
  fetchExceptionsData: (providedExceptionPayload?: Partial<FetchIssueExceptionPayload>) => void;
};

export type UseIssueExceptionsProps = {
  projectId?: string;
  releaseGroupId?: number;
  category: Category.Licensing | Category.Vulnerability | Category.Quality,
};

export type QueryFilters = {
  page: number;
  count: number;
  search: string;
  sortBy: IssueExceptionSort;
  orderBy: IssueExceptionOrderBy;
};

// This function is used to clean the store object and remove any keys that are not part of the QueryFilters type
// Additionally, it will cast the values to the correct type
const parseStore = (store: Record<string, unknown>): QueryFilters => {
  return Object.entries(store).reduce((acc, [key, value]) => {
    if (key in queryDefaults) {
      if (key === 'page' || key === 'count') {
        acc[key] = parseInt(value as string, 10);
      } else {
        // @ts-expect-error - we know this is a valid key and the correct corresponding type on the value
        acc[key] = value;
      }
    }
    return acc;
  }, {} as QueryFilters);
};

const queryDefaults: QueryFilters = {
  page: 1,
  count: 10,
  search: '',
  sortBy: ISSUE_EXCEPTION_PACKAGE_SORT,
  orderBy: 'asc',
};

const parserFn = (store: Record<string, unknown>): QueryFilters => ({
  ...queryDefaults,
  ...parseStore(store),
});

const useIssueExceptions = ({
  projectId,
  releaseGroupId,
  category,
}: UseIssueExceptionsProps): UseIssueExceptionsResponse => {
  const [filters, setFilters] = useQuery<QueryFilters>(parserFn);
  const { page, count, search, sortBy, orderBy } = filters;

  const dispatch = useDispatch();

  useDispatchOnMount(
    IssuesV2Store.actions.fetchIssueExceptions,
    {
      filters: { projectId, releaseGroupId, category },
      pagination: { page, count },
      search,
      sortBy,
      orderBy,
    },
  );

  const getIssueExceptionPayload = (): FetchIssueExceptionPayload => ({
    filters: { projectId, releaseGroupId, category },
    pagination: { page, count },
    search,
    sortBy,
    orderBy,
  });

  const fetchExceptionsData = (providedExceptionPayload: Partial<FetchIssueExceptionPayload> = {}) => {
    const defaultExceptionPayload = getIssueExceptionPayload();
    dispatch(
      IssuesV2Store.actions.fetchIssueExceptions({
        ...defaultExceptionPayload,
        ...providedExceptionPayload,
      }),
    );
  };

  // This must be wrapped in useCallback for debouncing to work properly
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchExceptionsData = useCallback(_.debounce(fetchExceptionsData, 250), []);

  const onRemoveExceptions = async (removeCriteria: IssueExceptionsRemoveRequest): Promise<number> => {
    const deletedCount = await dispatch(
      IssuesV2Store.actions.removeIssueExceptions({
        ...removeCriteria,
        search,
      }),
    ) as unknown as number;

    // refetch the data to update the UI. NOTE: we store the search, sort, and pagination state in the component, so
    // we call this function here instead of in redux to avoid having to pass all that state around
    await fetchExceptionsData();

    return deletedCount;
  };

  const onExtendException = async (exceptionId: number, expiresAfter: string | null) => {
    await dispatch(IssuesV2Store.actions.extendIssueException(exceptionId, expiresAfter));

    await fetchExceptionsData();
  };

  const setPage = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
    fetchExceptionsData({ pagination: { page: newPage, count } });
  };

  const setCount = (newCount: number) => {
    setFilters({ ...filters, count: newCount, page: 1 });
    fetchExceptionsData({ pagination: { page: 1, count: newCount } });
  };

  const setSearch = (newSearch: string) => {
    setFilters({ ...filters, search: newSearch, page: 1 });
    // debounce this to avoid making too many requests
    debouncedFetchExceptionsData({ search: newSearch });
  };

  const setSort = (newSortBy: IssueExceptionSort) => {
    const oldSortBy = sortBy;
    let newOrderBy = orderBy;

    // If they're sorting on a field that we already are sorting on, flip the ordering, otherwise set it to ascending
    if (oldSortBy === newSortBy) {
      newOrderBy = newOrderBy === 'asc' ? 'desc' : 'asc';
    } else {
      newOrderBy = 'asc';
    }

    setFilters({ ...filters, sortBy: newSortBy, orderBy: newOrderBy, page: 1 });
    fetchExceptionsData({ sortBy: newSortBy, orderBy: newOrderBy });
  };

  return {
    page,
    count,
    search,
    sortBy,
    setPage,
    setCount,
    setSearch,
    setSort,
    onRemoveExceptions,
    onExtendException,
    fetchExceptionsData,
  };
};

export default useIssueExceptions;
